import type { UserAssetV2Record } from "@apis/http/api";
import type { FocusType, VideoType } from "../librarySlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { UseImageBatchSelectionReturn } from "./useImageBatchSelection";

export const useVideoBatchSelection = (
  videoItems: UserAssetV2Record[],
  focusType: FocusType,
  videoType: VideoType,
): UseImageBatchSelectionReturn => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    if (focusType !== "video") {
      setSelectedIds(new Set());
    }
  }, [focusType]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [videoType]);

  useEffect(() => {
    const validIds = new Set(videoItems.map((item) => item.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [videoItems]);

  const toggle = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const setSelected = useCallback((id: number, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback((id: number) => selectedIds.has(id), [selectedIds]);

  const selectedCount = selectedIds.size;
  const selectionActive = selectedCount > 0;

  return useMemo(
    () => ({
      selectedIds,
      selectedCount,
      selectionActive,
      isSelected,
      toggle,
      setSelected,
      clear,
    }),
    [selectedIds, selectedCount, selectionActive, isSelected, toggle, setSelected, clear],
  );
};
